import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import GenericModal from "../components/GenericModal";
import api from "../../../src/api_interceptor/api";
import Loader from "../../components/loader/Loader";
import {
  sanitizeInput,
  sanitizeName,
} from "../../components/CommonFunction/CommonFunction";

const DietsForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const hasFetched = useRef(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    code: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const [modalState, setModalState] = useState({
    open: false,
    title: "",
    description: "",
    type: "success",
  });

  const showSuccess = (message) => {
    setModalState({
      open: true,
      title: "Success",
      description: message,
      type: "success",
    });
  };

  const showError = (message) => {
    setModalState({
      open: true,
      title: "Error",
      description: message || "Something went wrong. Please try again.",
      type: "error",
    });
  };

  useEffect(() => {
    if (isEditMode && !hasFetched.current) {
      hasFetched.current = true;
      getEditData();
    }
  }, [isEditMode, id]);

  const getEditData = async () => {
    setIsLoading(true);
    try {
      const API_URL = "api/GetAllDetailsToEditDiet/";
      const req_body = {
        Id: parseInt(id),
      };

      const response = await api.post(API_URL, req_body, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (response && response.data) {
        if (response.data.Message === "Success" || response.data.ID) {
          setFormData({
            name: response.data.Name || "",
            description: response.data.Description || "",
            code: response.data.Code || "",
          });
        } else {
          showError(
            response.data.Message || "Failed to load diet details."
          );
        }
      } else {
        showError("Invalid response from server. Please try again.");
      }
    } catch (error) {
      console.error("Get edit diet data error:", error);
      if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        showError(
          "Unable to connect to server. Please check your internet connection."
        );
      } else if (error.response) {
        const errorMsg =
          error.response.data?.Message ||
          "Failed to load diet details. Please try again.";
        showError(errorMsg);
      } else {
        showError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = "Name is mandatory";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitForm = async () => {
    setIsLoading(true);
    setSaving(true);

    const API_URL = "api/AddOrEditDiet/";
    const req_body = {
      Id: isEditMode ? parseInt(id) : 0,
      Name: formData.name.trim(),
      Description: formData.description?.trim() || "",
    };

    try {
      const response = await api.post(API_URL, req_body, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (response && response.data) {
        if (response.data.StatusCode === 1) {
          showSuccess(
            isEditMode
              ? "Diet updated successfully."
              : "Diet added successfully."
          );
          setShowSaveModal(false);
        } else if (response.data.StatusCode === 0) {
          const errorMsg =
            response.data.Message ||
            "Failed to save diet. Please try again.";
          showError(errorMsg);
        } else if (response.data.StatusCode === 3) {
          const errorMsg =
            response.data.Message ||
            "An error occurred while saving diet. Please try again.";
          showError(errorMsg);
        } else {
          showError("Invalid response from server. Please try again.");
        }
      } else {
        showError("Invalid response from server. Please try again.");
      }
    } catch (error) {
      console.error("Submit diet error:", error);

      if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        showError(
          "Unable to connect to server. Please check your internet connection."
        );
      } else if (error.response) {
        const errorMsg =
          error.response.data?.Message ||
          "Failed to save diet. Please try again.";
        showError(errorMsg);
      } else {
        showError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
      setSaving(false);
      setShowSaveModal(false);
    }
  };

  const handleCancel = () => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = () => {
    navigate("/diet-master");
  };

  return (
    <>
      {isLoading && <Loader />}

      <div className="bg-white rounded-lg p-6">
        <div className="mb-4">
          <h1 className="text-xl font-semibold">
            {isEditMode ? "Edit Diet" : "Add Diet"}
          </h1>

          <nav className="text-sm mt-1">
            <span
              className="text-blue-500 cursor-pointer"
              onClick={() => navigate("/diet-master")}
            >
              Diets
            </span>
            <span className="mx-1 text-gray-400">&gt;</span>
            <span className="text-primary">
              {isEditMode ? formData.code || "Edit Diet" : "Add Diet"}
            </span>
          </nav>
        </div>

        <div className="overflow-y-auto max-h-[calc(100vh-16rem)] pl-1 pr-6 custom-scrollbar">
          <div className="mb-8">
            <h2 className="text-base font-semibold text-primary mb-4">
              Section 1: Basic Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Code</label>
                <input
                  placeholder="Enter code"
                  value={isEditMode ? formData.code || "--" : "--"}
                  disabled
                  className="w-full border rounded-md px-3 py-2 text-sm input-field"
                />
                <p className="text-xs mt-1">System generated code</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Name <span className="text-error">*</span>
                </label>
                <input
                  className={`w-full border rounded-md px-3 py-2 text-sm input-field ${
                    errors.name ? "input-error" : ""
                  }`}
                  placeholder="Enter diet name"
                  value={formData.name}
                  maxLength={200}
                  onChange={(e) => {
                    const sanitized = sanitizeName(e.target.value);
                    setFormData({ ...formData, name: sanitized });
                    setErrors((prev) => ({ ...prev, name: null }));
                  }}
                />
                <div className="min-h-[16px]">
                  {errors.name && (
                    <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                  )}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <textarea
                  rows={3}
                  maxLength={500}
                  className="w-full border rounded-md px-3 py-2 text-sm input-field resize-none"
                  placeholder="Enter description"
                  value={formData.description}
                  onChange={(e) => {
                    const sanitized = sanitizeInput(e.target.value, {
                      allowNumbers: true,
                      allowLetters: true,
                      allowSpaces: true,
                      allowPunctuation: true,
                    });
                    setFormData({ ...formData, description: sanitized });
                  }}
                />
                <div className="text-right text-xs mt-1">500 characters max</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <button
            className="w-[120px]"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            className="btn-save w-[120px]"
            onClick={() => {
              if (!validateForm()) return;
              setShowSaveModal(true);
            }}
            disabled={saving}
          >
            {isEditMode ? "Update" : "Save"}
          </button>
        </div>

        <GenericModal
          open={showCancelModal}
          title="Confirm Cancel"
          description="Are you sure to cancel and exit?"
          primaryText="Yes"
          secondaryText="No"
          onPrimary={handleCancelConfirm}
          onClose={() => setShowCancelModal(false)}
          disableBackdropClose={false}
        />

        <GenericModal
          open={showSaveModal}
          title={isEditMode ? "Update Diet" : "Save Diet"}
          description={`Are you sure you want to ${
            isEditMode ? "update" : "save"
          } this diet?`}
          primaryText={isEditMode ? "Update" : "Save"}
          secondaryText="Cancel"
          loading={saving}
          onPrimary={submitForm}
          onClose={() => {
            if (!saving) {
              setShowSaveModal(false);
            }
          }}
          disableBackdropClose={saving}
        />

        <GenericModal
          open={modalState.open}
          title={modalState.title}
          description={modalState.description}
          type={modalState.type}
          primaryText="OK"
          onPrimary={() => {
            const wasSuccess = modalState.type === "success";
            setModalState((prev) => ({ ...prev, open: false }));
            if (wasSuccess) {
              navigate("/diet-master");
            }
          }}
          onClose={() => {
            const wasSuccess = modalState.type === "success";
            setModalState((prev) => ({ ...prev, open: false }));
            if (wasSuccess) {
              navigate("/diet-master");
            }
          }}
        />
      </div>
    </>
  );
};

export default DietsForm;
